import React, { useEffect } from 'react';

import { Box, Icon } from '@amzn/awsui-components-react-v3';

import { isArray } from 'lodash';

import { useMetrics } from '../../appSelectorContext';
import { METRIC_PUBLISHER_NAMES } from '../../constants';
import { Model } from '../../store/types';
import { WithExceptions } from '../ExceptionsModal/WithExceptions';
import { UNFIED_RECEIVE_CTI_URL } from '../ExceptionsModal/constants';
import { BLOCKING_ERRORS, MODEL_ERROR_CODES, MODEL_ERROR_CODES_MAPPING, ORDER_NOT_FOUND_METRIC, PO_CLOSED_METRIC, LINE_ITEM_CLOSED_METRIC, ORDER_LINE_ITEM_NOT_FOUND_METRIC, NOT_ACTIVE_LINE_ITEM_METRIC, NOT_ACTIVE_REFERENCE_METRIC } from './constants';

export const NotInCatalogue = ({ referenceId, scannedModelId }: { referenceId: string; scannedModelId: string }) => {
  return (
    <Box color="text-status-error">
      {scannedModelId} - {MODEL_ERROR_CODES_MAPPING.model_not_exists_catalogue}
    </Box>
  );
};

export const ClientError = () => {
  return (
    <Box color="text-status-error">
      {MODEL_ERROR_CODES_MAPPING.runtime_client_error} click <a href={UNFIED_RECEIVE_CTI_URL}>here</a> to cut a ticket
      for help.
    </Box>
  );
};

export const AllLineItemsClosed = ({
  referenceId,
  scannedModelId,
  models = [],
  isPO,
}: {
  referenceId: string;
  scannedModelId: string;
  models: any;
  isPO: boolean;
}) => {
  const context = useMetrics();
  const errors = models?.reduce(
    (o, c) => {
      const lineItemNumber = c?.refereceLineItem?.externalReferenceLineItemNumber ?? '';
      const referenceValue = c?.refereceLineItem?.externalReferenceValue ?? '';

      const orderNumber = (isPO ? referenceValue : c?.refereceLineItem?.orderNumber) ?? '';
      const orderLineItemNumber = (isPO ? lineItemNumber : c?.refereceLineItem?.orderLineItemNumber) ?? '';
      const orderId = isPO ? c?.refereceLineItem?.referenceId : c?.refereceLineItem?.orderReferenceId;
      const orderLineItemId = isPO
        ? c?.refereceLineItem?.referenceLineItemId
        : c?.refereceLineItem?.orderReferenceLineItemId;

      const orderIdPresent = !!orderId;
      const orderLineItemIdPresent = !!orderLineItemId;
      const statusCode = c?.status?.statusCode;

      if (statusCode === 'INACTIVE_RECEIVE_REFERENCE_ORDER') {
        o.poClosed = o.poClosed.concat(orderNumber || '');
      }

      if (statusCode === 'INACTIVE_REFERENCE_LINE_ITEM_ORDER') {
        const lineItem = orderNumber + ' Line #' + orderLineItemNumber;
        o.lineItemClosed = o.lineItemClosed.concat(lineItem || '');
      }

      if (statusCode === 'INACTIVE_REFERENCE_LINE_ITEM') {
        if (isPO) {
          const lineItem = orderNumber + ' Line #' + orderLineItemNumber;
          o.notActiveLineItem = o.notActiveLineItem.concat(lineItem || '');
        } else if (!orderIdPresent) {
          o.orderNotFound = o.orderNotFound.concat(orderNumber || '');
        } else if (!orderLineItemIdPresent) {
          const lineItem = orderNumber + ' Line #' + orderLineItemNumber;
          o.orderLineItemNotFound = o.orderLineItemNotFound.concat(lineItem || '');
        } else {
          const lineItem = referenceValue + ' Line #' + lineItemNumber;
          o.notActiveLineItem = o.notActiveLineItem.concat(lineItem || '');
        }
      }

      if (statusCode === 'INACTIVE_RECEIVE_REFERENCE') {
        o.notActiveReference = o.notActiveReference.concat(c?.refereceLineItem?.externalReferenceValue || '');
      }

      return o;
    },
    {
      poClosed: [],
      lineItemClosed: [],
      notActiveLineItem: [],
      notActiveReference: [],
      orderNotFound: [],
      orderLineItemNotFound: [],
    },
  );

  let errorMessage = '';

  // For ASN line items, PO(s) are closed but not the order line items
  if (errors?.poClosed.length > 0 && errors?.lineItemClosed.length === 0) {
    errorMessage += ` Model is part of POs (${errors?.poClosed.join(',')}) that are closed or cancelled,`;
  }

  // For ASN line items, Order line item(s) are closed but not the PO(s)
  if (errors?.lineItemClosed.length > 0 && errors?.poClosed.length === 0) {
    errorMessage += ` Model is part of POs (${errors?.lineItemClosed.join(',')}) that are closed or cancelled,`;
  }

  // For ASN line items, Order line item(s) and PO(s) are closed
  if (errors?.lineItemClosed.length > 0 && errors?.poClosed.length > 0) {
    errorMessage += ` Model is part of POs (${errors?.poClosed.join(',')}, ${errors?.lineItemClosed.join(
      ',',
    )}) that are closed or cancelled,`;
  }

  // If inactive line item is returned, and the order reference is not found
  if (errors?.orderNotFound.length > 0) {
    errorMessage += ` Model is associated with POs (${errors?.orderNotFound.join(', ')}) that are not present,`;
  }

  useEffect(() => {
    const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(METRIC_PUBLISHER_NAMES.SCAN_MPN);
    if (errors?.poClosed.length > 0) {
      publisher.publishCounterMonitor(PO_CLOSED_METRIC, 1);
    }
    if (errors?.lineItemClosed.length > 0) {
      publisher.publishCounterMonitor(LINE_ITEM_CLOSED_METRIC, 1);
    }
    if (errors?.orderNotFound.length > 0) {
      publisher.publishCounterMonitor(ORDER_NOT_FOUND_METRIC, 1);
    }
    if (errors?.orderLineItemNotFound.length > 0) {
      publisher.publishCounterMonitor(ORDER_LINE_ITEM_NOT_FOUND_METRIC, 1);
    }
    if (errors?.notActiveLineItem.length > 0) {
      publisher.publishCounterMonitor(NOT_ACTIVE_LINE_ITEM_METRIC, 1);
    }
    if (errors?.notActiveReference.length > 0) {
      publisher.publishCounterMonitor(NOT_ACTIVE_REFERENCE_METRIC, 1);
    }
  }, [
    errors?.poClosed.length,
    errors?.lineItemClosed.length,
    errors?.orderNotFound.length,
    errors?.orderLineItemNotFound.length,
    errors?.notActiveLineItem.length,
    errors?.notActiveReference.length,
    context.initialMetricsPublisher,
  ]);

  // If inactive line item is returned, and the order reference is not found
  if (errors?.orderLineItemNotFound.length > 0) {
    errorMessage += ` Model is associated with lines on PO (${errors?.orderLineItemNotFound.join(
      ', ',
    )}) that are not present,`;
  }

  // If the PO or PO lines are not closed, the order is not found, and this is for POs
  if (errors?.notActiveLineItem.length > 0) {
    errorMessage += ` Model is associated with line items (${errors?.notActiveLineItem.join(
      ',',
    )}) that are inactive or closed.`;
  }

  if (errors?.notActiveReference?.length > 0) {
    errorMessage += `Line items are associated with references (${errors?.notActiveReference.join(
      ',',
    )}) that are closed or inactive.`;
  }
  return (
    <Box color="text-status-error">
      <Box variant="span" margin={{ right: 'xxs' }}>
        {errorMessage}
      </Box>
    </Box>
  );
};

export const ReferenceLineItemErrors = ({
  referenceId,
  code,
  model,
}: {
  referenceId: string;
  code: string;
  model: Model;
}) => {
  const scannedModelId = model.expectedMPN;
  return (
    <Box color="text-status-error">
      {code === 'INACTIVE_RECEIVE_REFERENCE_ORDER' && (
        <Box variant="span">
          {' '}
          {scannedModelId} - Underlying PO {model.orderNumber} is closed or cancelled.{' '}
        </Box>
      )}

      {code === 'INACTIVE_REFERENCE_LINE_ITEM_ORDER' && (
        <Box variant="span">
          {scannedModelId} -Underlying PO {model.orderNumber} line item {model.orderLineItemNumber} is closed or
          cancelled.{' '}
        </Box>
      )}
      {code !== 'INACTIVE_RECEIVE_REFERENCE_ORDER' && code !== 'INACTIVE_REFERENCE_LINE_ITEM_ORDER' && (
        <Box variant="span">
          {' '}
          {scannedModelId} - {MODEL_ERROR_CODES_MAPPING[code]}{' '}
        </Box>
      )}
    </Box>
  );
};

export const NotInASN = () => {
  return (
    <Box variant="span">
      {MODEL_ERROR_CODES_MAPPING.model_not_exists_asn}, proceed with receive parts from this model
    </Box>
  );
};

export const DIifferentPOLineItem = () => {
  return (
    <Box color="text-status-error">
      model123 {MODEL_ERROR_CODES_MAPPING.model_in_difference_po_line} po123
      <a href="/inventory-receive/receive/po123/model/model123" target="_blank">
        <Box color="text-status-error" fontSize="body-m">
          receive against po <Icon name="external" />
        </Box>
      </a>
    </Box>
  );
};

export const ErrorMessage = ({
  code,
  referenceId,
  scannedModelId,
  selectedModel,
  isPO = false,
}: {
  code: string;
  referenceId?: string;
  scannedModelId?: string;
  selectedModel?: Model;
  isPO?: boolean;
}) => {
  if (code === MODEL_ERROR_CODES.model_not_exists_catalogue) {
    return <NotInCatalogue referenceId={referenceId} scannedModelId={scannedModelId} />;
  }

  if (code === MODEL_ERROR_CODES.model_not_exists_asn) {
    return <NotInASN />;
  }

  if (code === MODEL_ERROR_CODES.model_in_difference_po_line) {
    return <DIifferentPOLineItem />;
  }

  if (code === 'all_line_items_closed') {
    return (
      <AllLineItemsClosed
        scannedModelId={scannedModelId}
        models={isArray(selectedModel) ? selectedModel : []}
        isPO={isPO}
      />
    );
  }

  if (BLOCKING_ERRORS.includes(code)) {
    return <ReferenceLineItemErrors code={code} model={selectedModel} />;
  }

  return MODEL_ERROR_CODES_MAPPING[code] || MODEL_ERROR_CODES_MAPPING.network_error;
};

export const ErrorMessages = WithExceptions(ErrorMessage);
